// import APIs
import { mutations } from 'RestAPIs/rapini';

// import atoms
import { planResponsePlanningEntityIdentifiersAtom } from 'components/roster-portal/activity-planning/atoms';

// import components
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import AssignEmployeeDialog from 'components/roster-portal/activity-planning/components/AssignEmployeeDialog';
import { ContextMenu } from 'components/roster-portal/activity-planning/components/ContextMenu';
import { ContextMenuOptions } from 'components/roster-portal/activity-planning/components/ContextMenu/types';
import EditRouteDialog from 'components/roster-portal/activity-planning/components/EditRouteDialog/EditRouteDialog';
import { ActionTooltip } from 'components/shared';

// import constants
import { MAPVIEW_SESSION_STORAGE_KEYS } from 'components/roster-portal/activity-planning/AzureMaps/types';

// import enums
import { DialogId } from './ScheduleContextMenu.enums';

// import external libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { useSessionStorage } from 'usehooks-ts';
import { Box } from '@mui/material';

// import types
import { RemoveAssignmentEmploymentToAppointmentGroupingRequest } from 'generated';
import { ScheduleContextMenuProps } from './ScheduleContextMenu.types';

// import utils
import { refetchPlanResponsePlanningEntityIdentifiers } from 'components/roster-portal/activity-planning/utils';

export default function ScheduleContextMenu({
  appointmentGroupingEntityId,
  appointmentGroupingName,
  appointmentGroupingStart,
  clickType,
  events,
  employeeId,
  isAssignableToEmployee = false,
  menuSx,
  onConnectClick,
  onConnectScheduleClick,
  onDialogOpen,
  onOpen,
  onChangeMousePosition,
  transportationMode,
  scheduleId,
  sx,
  optionsToHide = [],
  cursorPosition = null,
  planningEntityIdentifier
}: ScheduleContextMenuProps) {
  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning.routeContextMenu'
  });
  const { t: tActivity } = useTranslation('common', {
    keyPrefix: 'activityPlanning'
  });
  const firstAppointment = events ? events[0] : undefined;
  const [activeDialogId, setActiveDialogId] = useState<DialogId | null>(null);
  const [, setFilteredSchedule] = useSessionStorage<{
    start: string;
    scheduleId: string;
  }>(MAPVIEW_SESSION_STORAGE_KEYS.FILTERED_SCHEDULE, {
    start: '',
    scheduleId: ''
  });
  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const isRouteSchedule = !!appointmentGroupingEntityId;

  const { mutate } = mutations.useUpdateAppointmentGroupingTransportationMode(
    appointmentGroupingEntityId ?? ''
  );

  const { mutate: removeAssignmentEmploymentToAppointmentGrouping } =
    mutations.useRemoveAssignmentEmploymentToAppointmentGrouping(
      appointmentGroupingEntityId ?? ''
    );

  const options = [
    ...(isRouteSchedule ? [ContextMenuOptions.CONNECT_ROUTE] : []),
    ...(!isRouteSchedule && planningEntityIdentifier
      ? [ContextMenuOptions.CONNECT_SCHEDULE]
      : []),
    ...(isRouteSchedule && firstAppointment
      ? [ContextMenuOptions.EDIT_ROUTE_START]
      : []),
    ...(isRouteSchedule && transportationMode
      ? transportationMode === 'CAR'
        ? [ContextMenuOptions.CHANGE_TO_BICYCLE]
        : [ContextMenuOptions.CHANGE_TO_CAR]
      : []),
    ...(isRouteSchedule && isAssignableToEmployee
      ? [ContextMenuOptions.CONNECT_EMPLOYEE]
      : []),
    ...(isRouteSchedule && isAssignableToEmployee && employeeId
      ? [
          ContextMenuOptions.REMOVE_ASSIGNMENT_EMPLOYMENT_TO_APPOINTMENT_GROUPING
        ]
      : []),
    ...(isRouteSchedule ? [ContextMenuOptions.SHOW_ROUTE_ON_MAP] : [])
  ]
    .filter((option) => !optionsToHide.includes(option as ContextMenuOptions))
    .map((option) => t(option));

  function onContextMenuItemClick(option: string) {
    switch (option) {
      case t(ContextMenuOptions.CONNECT_ROUTE):
        onConnectClick?.();
        break;
      case t(ContextMenuOptions.CONNECT_SCHEDULE):
        setActiveDialogId(DialogId.CONNECT_SCHEDULE_RECURRING);
        break;
      case t(ContextMenuOptions.CHANGE_TO_BICYCLE):
        mutate(
          { transportationMode: 'BICYCLE' },
          {
            onSuccess: () => {
              showSuccessSnackbar();
              refetchPlanResponsePlanningEntityIdentifiers(
                setPlanResponsePlanningEntityIdentifiers
              );
            }
          }
        );
        break;
      case t(ContextMenuOptions.CHANGE_TO_CAR):
        mutate(
          { transportationMode: 'CAR' },
          {
            onSuccess: () => {
              showSuccessSnackbar();
              refetchPlanResponsePlanningEntityIdentifiers(
                setPlanResponsePlanningEntityIdentifiers
              );
            }
          }
        );
        break;
      case t(ContextMenuOptions.CONNECT_EMPLOYEE):
        setActiveDialogId(DialogId.CONNECT_EMPLOYEE);
        break;
      case t(ContextMenuOptions.EDIT_ROUTE_START):
        setActiveDialogId(DialogId.EDIT_ROUTE);
        break;
      case t(ContextMenuOptions.SHOW_ROUTE_ON_MAP):
        onShowRouteOnMap();
        break;
      case t(
        ContextMenuOptions.REMOVE_ASSIGNMENT_EMPLOYMENT_TO_APPOINTMENT_GROUPING
      ): {
        const payload: RemoveAssignmentEmploymentToAppointmentGroupingRequest =
          {
            date: appointmentGroupingStart ?? ''
          };
        removeAssignmentEmploymentToAppointmentGrouping(payload, {
          onSuccess: () => {
            showSuccessSnackbar();
            refetchPlanResponsePlanningEntityIdentifiers(
              setPlanResponsePlanningEntityIdentifiers
            );
          }
        });
        break;
      }
    }
  }

  function handleDialogClose() {
    setActiveDialogId(null);
  }

  function onShowRouteOnMap() {
    setFilteredSchedule({
      start: appointmentGroupingStart ?? '',
      scheduleId
    });
    window.open('/#roster,activityPlanning&view=map', '_self');
  }

  useEffect(() => {
    onDialogOpen?.(!!activeDialogId);

    return () => {
      onDialogOpen?.(false);
    };
  }, [activeDialogId, onDialogOpen]);

  return (
    <>
      <ContextMenu
        options={options}
        onOptionClick={onContextMenuItemClick}
        onOpen={(open, event) => {
          onOpen?.(open);
          onChangeMousePosition?.(event?.clientX || 0, event?.clientY || 0);
        }}
        clickType={clickType}
        sx={sx}
        menuSx={menuSx}
        cursorPosition={cursorPosition}
      />
      {activeDialogId === DialogId.CONNECT_EMPLOYEE &&
        appointmentGroupingEntityId && (
          <AssignEmployeeDialog
            open={activeDialogId === DialogId.CONNECT_EMPLOYEE}
            handleClose={handleDialogClose}
            appointmentGroupingEntityId={appointmentGroupingEntityId}
            date={appointmentGroupingStart ?? ''}
            appointmentGroupingName={appointmentGroupingName ?? ''}
          />
        )}
      {activeDialogId === DialogId.EDIT_ROUTE &&
        appointmentGroupingEntityId &&
        events?.length &&
        events.find(
          ({ category }) => category === 'NON_CLIENT' || category === 'CLIENT'
        ) && (
          <EditRouteDialog
            appointmentGroupingEntityId={appointmentGroupingEntityId}
            appointmentGroupingStart={appointmentGroupingStart ?? ''}
            open={activeDialogId === DialogId.EDIT_ROUTE}
            events={events}
            handleClose={handleDialogClose}
          />
        )}
      <ActionTooltip
        title={tActivity('editRecurringAppointment')}
        open={activeDialogId === DialogId.CONNECT_SCHEDULE_RECURRING}
        handleClose={handleDialogClose}
        actionButtons={[
          {
            label: tActivity('onlyThisAppointment'),
            variant: 'outlined',
            onClick: () => {
              onConnectScheduleClick?.('ONLY_THIS');
              handleDialogClose();
            }
          },
          {
            label: tActivity('thisAndSubsequentAppointments'),
            variant: 'outlined',
            onClick: () => {
              onConnectScheduleClick?.('THIS_AND_ALL_SUBSEQUENT');
              handleDialogClose();
            }
          }
        ]}
      >
        <Box />
      </ActionTooltip>
    </>
  );
}
